import { TableCell, TableRow } from '@/shared/components/ui/Table';
import {
  Interaction,
  InteractionType,
  FeedbackInteraction,
  AllReasonOption,
  AllGoalTypes,
} from '@momentum/types';
import { AlertCircle, Calendar, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InteractionTypeBadge } from './InteractionTypeBadge';
import { InteractionStatusBadge } from './InteractionStatusBadge';
import { InteractionGoal } from './InteractionGoal';
import { formatDefaultDate } from '@/shared/utils/date-utils';

interface InteractionRowProps {
  interaction: Interaction;
  participants: string[];
  reason: AllReasonOption | null;
  showAlert: boolean;
  goals: AllGoalTypes[];
  onClick: (interaction: Interaction) => void;
}

export const InteractionRow = ({
  interaction,
  participants,
  reason,
  showAlert,
  goals,
  onClick,
}: InteractionRowProps) => {
  const { t, i18n } = useTranslation('interactions');
  const { t: tWizard } = useTranslation('wizard');

  return (
    <TableRow
      onClick={() => onClick(interaction)}
      className="hover:bg-muted/50 transition-colors cursor-pointer group"
    >
      <TableCell className="py-4">
        <div className="flex items-center gap-2">
          <InteractionTypeBadge
            type={interaction.type}
            isDraft={interaction.isDraft}
          />
          {showAlert && (
            <div className="relative group/tooltip">
              <AlertCircle className="h-4 w-4 text-orange-500 animate-pulse" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                {t('actionRequired')}
              </div>
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="py-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium text-foreground">
            {participants.join(', ')}
          </span>
        </div>
      </TableCell>
      <TableCell className="py-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground">
            {formatDefaultDate(
              interaction.metadata.createdAt,
              i18n.language as 'en' | 'he'
            )}
          </span>
        </div>
      </TableCell>
      <TableCell className="py-4">
        {interaction.type === InteractionType.FEEDBACK && (
          <div>
            <InteractionStatusBadge
              status={(interaction as FeedbackInteraction).status}
            />
          </div>
        )}
      </TableCell>
      <TableCell className="py-4">
        <InteractionGoal goalId={interaction.goal?.id} goals={goals} />
      </TableCell>
      <TableCell className="py-4">
        {reason && (
          <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
            {tWizard(`reasonConstants.${reason}`)}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
};
